
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface FloatingChar {
  id: number;
  char: string;
  x: number;
  size: number;
  duration: number;
  delay: number;
  opacity: number;
  color: string;
}

const PIXEL_CHARS = [
  'A','I','D','A','T','A','0','1','A','I',
  'M','L','G','P','T','B','I','G','D','A','T','A',
  '∑','∞','λ','π','Δ','Ω','α','β','γ',
  '0','1','0','1','1','0','0','1',
  'A','I','D','X','R','O','I',
];

const COLORS = [
  'rgba(45,212,191,VAL)',   // teal-400
  'rgba(52,211,153,VAL)',   // emerald-400
  'rgba(110,231,183,VAL)',  // emerald-300
  'rgba(167,243,208,VAL)',  // emerald-200
  'rgba(255,255,255,VAL)',  // white
];

function generateChars(count: number): FloatingChar[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    char: PIXEL_CHARS[Math.floor(Math.random() * PIXEL_CHARS.length)],
    x: Math.random() * 100,
    size: Math.random() * 14 + 10,
    duration: Math.random() * 12 + 10,
    delay: Math.random() * 15,
    opacity: Math.random() * 0.5 + 0.15,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
  }));
}

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [chars, setChars] = useState<FloatingChar[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    const timer = setTimeout(() => setIsLoaded(true), 100);
    setChars(generateChars(38));
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const handleScrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative min-h-screen flex items-center overflow-hidden">
      {/* 全域 keyframes */}
      <style>{`
        @keyframes floatUp {
          0% {
            transform: translateY(110vh) scale(0.8);
            opacity: 0;
          }
          5% { opacity: 1; }
          90% { opacity: 1; }
          100% {
            transform: translateY(-10vh) scale(1.05);
            opacity: 0;
          }
        }
        @keyframes pixelGlow {
          0%, 100% { text-shadow: 0 0 4px currentColor; }
          50% { text-shadow: 0 0 12px currentColor, 0 0 24px currentColor; }
        }
        .pixel-char {
          font-family: 'Courier New', 'Lucida Console', monospace;
          font-weight: 700;
          animation: floatUp linear infinite, pixelGlow ease-in-out infinite;
          position: absolute;
          bottom: -10%;
          user-select: none;
          pointer-events: none;
          letter-spacing: 0.05em;
          image-rendering: pixelated;
        }
      `}</style>

      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/ee777c0d97369c89741a69066bdfc67f.jpg"
          alt="Hero Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
      </div>

      {/* 飄浮 pixel 字母層 — 僅在右半側顯示 */}
      <div
        ref={containerRef}
        className="absolute inset-0 z-10 pointer-events-none overflow-hidden"
        style={{ left: '50%', width: '50%' }}
      >
        {chars.map((c) => {
          const colorStr = c.color.replace('VAL', String(c.opacity));
          return (
            <span
              key={c.id}
              className="pixel-char"
              style={{
                left: `${c.x}%`,
                fontSize: `${c.size}px`,
                color: colorStr,
                animationDuration: `${c.duration}s, ${c.duration * 0.6}s`,
                animationDelay: `${c.delay}s, ${c.delay * 0.4}s`,
              }}
            >
              {c.char}
            </span>
          );
        })}
      </div>

      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'}`}>
        <div className="px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/21854ded7812c3e1f6e0237d0c8099eb/b16dfe45d3df984f479dd6ea789b5412.png"
                alt="恩誠國際 Logo"
                className="w-9 h-9 object-contain"
              />
            </div>
            <div>
              <div className={`font-bold text-base leading-tight transition-colors duration-500 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>恩誠國際</div>
              <div className={`text-xs leading-tight transition-colors duration-500 ${isScrolled ? 'text-gray-400' : 'text-white/70'}`}>En-Cheng International</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {[
              { path: '/', label: '首頁' },
            ].map((item) => (
              <Link key={item.path} to={item.path} className={`font-medium text-sm cursor-pointer transition-colors duration-300 ${isScrolled ? 'text-gray-600 hover:text-teal-600' : 'text-white/90 hover:text-white'}`}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-8 pt-24">
        <div className="max-w-3xl">
          <div className={`inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-md rounded-full text-teal-300 mb-8 border border-teal-400/30 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <i className="ri-sparkling-line"></i>
            <span className="text-sm font-medium">AI 企業數位轉型專家</span>
          </div>

          <h1 className={`text-6xl font-extrabold text-white leading-tight mb-6 transition-all duration-700 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            以 AI 驅動<br />
            <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">企業數位轉型</span><br />
            提升競爭優勢
          </h1>

          <p className={`text-lg text-white/80 leading-relaxed mb-10 max-w-xl transition-all duration-700 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            恩誠國際提供數據分析與預測、客製化微型工具、AI自動化工具設計，協助企業突破效率瓶頸，在數位時代取得領先地位。
          </p>

          <div className={`flex items-center gap-4 transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <a
              href="mailto:enchengai.com@gmail.com"
              className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-semibold text-sm whitespace-nowrap cursor-pointer shadow-xl shadow-teal-500/40 hover:shadow-2xl hover:from-teal-600 hover:to-emerald-600 transition-all hover:scale-105 flex items-center gap-2"
            >
              立即免費諮詢
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
            <button
              onClick={handleScrollToServices}
              className="px-8 py-4 bg-white/10 backdrop-blur-md text-white rounded-lg font-semibold text-sm whitespace-nowrap cursor-pointer border border-white/30 hover:bg-white/20 transition-all hover:scale-105 flex items-center gap-2"
            >
              <i className="ri-play-circle-line text-lg"></i>
              了解服務
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
